import { Button } from "@/components/ui/button";
import { useApp } from "@/app/app-context";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import DealersTextDark from "@/assets/dealers-text-dark.svg";
import AssistantDirection from "@/assets/icons/assistant-direction.svg";
import AppLogo from "@/assets/app-logo.svg";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth-store";

export default function HomePage() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { toast } = useToast();

  const { apps } = useApp();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = () => {
    try {
      setIsLoggingOut(true);
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        variant: "destructive",
        title: "Logout failed",
        description: "An error occurred while logging out. Please try again.",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#F5F7FA]">
      <div
        style={{
          position: "fixed",
          opacity: 0.1,
          backgroundImage: `url('/app-bg.svg')`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "120% 90%",
          width: "100%",
          height: "100%",
          boxShadow: "0px 0px 12px 6px #00000033",
          zIndex: 0,
        }}
      ></div>

      {/* Header */}
      <header className="mx-auto px-20 py-9 flex justify-between items-center z-10">
        <div className="flex items-center gap-2">
          <DealersTextDark className="w-64 h-fit" />
        </div>
        <div className="flex items-center z-10">
          <Button
            size="sm"
            variant="ghost"
            className="flex items-center gap-2 hover:bg-slate-200"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <LogOut className="w-4 h-4" />
            {isLoggingOut ? "Logging out..." : "Logout"}
          </Button>
        </div>
      </header>

      <main className="mx-auto px-24 py-4 lg:py-16">
        <div className="flex flex-col lg:flex-row justify-between gap-5 lg:gap-20">
          <div
            className="flex-1 p-6 h-fit bg-[#3478F4] bg-no-repeat bg-center rounded-md z-10"
            style={{
              backgroundImage: `
        linear-gradient(0deg, #3478F4, #3478F4),
        radial-gradient(100% 100% at 50% 0%, rgba(255, 255, 255, 0.15) 0%, rgba(150, 150, 150, 0) 50%, rgba(0, 0, 0, 0.075) 100%),
        linear-gradient(78.54deg, rgba(0, 0, 0, 0.2) -82.45%, rgba(0, 0, 0, 0) 25.22%),
        linear-gradient(275.27deg, rgba(0, 0, 0, 0.2) -40.32%, rgba(0, 0, 0, 0) 21.43%)
      `,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-2xl text-primary-foreground font-bold">
                Pick your destination
              </h1>
              <AssistantDirection className="w-6 h-6" />
            </div>
            <p className="text-primary-foreground mb-6">
              Choose an App to access
            </p>
            <div className="flex flex-wrap items-start gap-4 p-4 bg-[#E2E8F066] rounded-md">
              {apps.length === 0 ? (
                <div className="w-full text-center py-8">
                  <p className="text-primary-foreground/80 text-sm">
                    No applications available. Contact your administrator for
                    access.
                  </p>
                </div>
              ) : (
                apps.map((app) => (
                  <Button
                    key={app.name}
                    onClick={() => navigate(app.basePath)}
                    className="cursor-pointer flex flex-col gap-2 py-2 px-2 h-fit w-20 bg-white 
                    shadow-sm hover:shadow-md transition-shadow  text-slate-500 
                    rounded-lg [&_svg]:size-16 [&_p]:hover:slate-800 hover:bg-slate-50 hover:text-slate-900
                    
                    "
                  >
                    <AppLogo />
                    <p
                      className="text-sm text-center whitespace-pre-wrap 
                    hover:scale-105 transition-transform
"
                    >
                      {app.name}
                    </p>
                  </Button>
                ))
              )}
            </div>
          </div>
          <div className="flex-1 max-w-full z-10">
            <div className="flex flex-col gap-4">
              <div
                className="relative rounded-lg p-6"
                style={{
                  backgroundImage: `url('/announcement-bg.jpg')`,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "65% 55%",
                  width: "100%",
                  height: "100%",
                  boxShadow: "0px 0px 12px 6px #00000033",
                }}
              >
                <div
                  className="rounded-lg absolute inset-0 bg-gradient-to-r from-[rgba(0,0,0,1)] 
                  from-15% to-[rgba(29,72,117,.1)]"
                ></div>
                <div className="z-10 drop-shadow-lg shadow-lg">
                  <h2 className="text-2xl text-primary-foreground font-bold mb-2">
                    Announcement Title
                  </h2>
                  <p className="text-sm text-primary-foreground opacity-80 mb-12">
                    Lorem ipsum dolor sit amet, consectetur adipisicing elit.
                    Perspiciatis voluptate beatae hic blanditiis ipsum, magni
                    temporibus aliquam facilis. Quaerat tempora assumenda qui
                    officia voluptatem nesciunt odit, facere harum dolor quam ad
                    ullam maiores. Aliquid est minima illo consequuntur quia
                    magni, aliquam adipisci numquam excepturi dolorum ex dolore
                    optio! Obcaecati fuga placeat ipsum aspernatur accusantium
                    delectus?
                  </p>
                  <Button>Check it out</Button>
                </div>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-2">Announcement</h2>
                <p className="text-sm opacity-90 mb-6">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt.
                </p>
                <Button variant="link" className="p-0.5 font-semibold">
                  Read more &gt;
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
